import {
  createElement,
  DependencyList,
  Fragment,
  memo,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";

import { getPortal, track, update } from "./store";

/**
 * Types
 */

interface IExitProps {
  name: string;
  onContent?: (hasContent: boolean) => void;
}
interface IEntranceProps {
  to: IExitProps["name"];
}

/**
 * Hooks
 */
export const useVirtualPortalExit = (name: string) => {
  const [node, setNode] = useState<ReactNode>(() => getPortal(name));
  useEffect(() => track(name, setNode), [name]);
  return useMemo(() => node, [node]);
};

export const useVirtualPortalEntrance = (
  name: string,
  factory: () => ReactNode,
  deps: DependencyList,
) => {
  const node = useMemo(factory, [factory, ...deps]);
  useEffect(() => update(name, node), [name, node]);
};

/**
 * Components
 */

const Exit = ({ name, onContent }: IExitProps) => {
  const node = useVirtualPortalExit(name);
  useEffect(() => {
    onContent && onContent(!!node);
  }, [node, onContent]);
  return useMemo(() => createElement(Fragment, {}, node), [node]);
};

const Entrance = memo(({ to, children }: PropsWithChildren<IEntranceProps>) => {
  useVirtualPortalEntrance(to, () => children, [children]);
  return null;
});

export const virtualPortal = {
  Entrance,
  Exit,
};
